import React, { useState, useEffect, useRef } from 'react';
import { compositeInfographic, compositeComic } from '../services/imageCompositor';
import Layout from '../components/Layout';
import { TypeformPipeline } from '../components/TypeformPipeline';
import { getLastSession, type DiscoveredTrend } from '../services/domainEngine';
import { mockTrends } from '../data/index';
import {
  generateContentBundle,
  getStoredBundles,
  updateBundleApproval,
  deleteBundle,
  getStoredBrandProfile,
  type GeneratedBundle,
} from '../services/openai';
import {
  Sparkles,
  FileText,
  Image,
  Video,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Trash2,
  Loader2,
  AlertCircle,
  Key,
  Copy,
  Check,
  BarChart2,
  BookOpen,
  ExternalLink,
  Download,
  Maximize2,
  X,
  Wand2,
  ZoomIn,
  Share2,
  MessageSquare,
  Film,
  Layers,
  FileSearch2,
  Rss,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

type TabType = 'blog' | 'infographic' | 'reel' | 'comic' | 'social' | 'visual' | 'video' | 'newsletter' | 'whitepaper';

/** Build a full blog post body that embeds the infographic and comic strip images
 *  as markdown image syntax (with captions), ready to paste into any CMS. */
function buildEnrichedBlogBody(
  bundle: GeneratedBundle,
  infographicUrl: string | null,
  comicUrl: string | null
): string {
  const body = bundle.blogPost?.body || '';
  if (!infographicUrl && !comicUrl) return body;

  // Split body at ~40% mark to insert infographic inline
  const paragraphs = body.split(/\n\n+/);
  const splitAt = Math.max(2, Math.floor(paragraphs.length * 0.4));

  const infographicBlock = infographicUrl
    ? `\n\n---\n![${bundle.infographic?.title || 'Infographic'}: ${bundle.trendName}](${infographicUrl})\n*📊 ${bundle.infographic?.subtitle || 'Key data points and insights — ' + bundle.trendName}*\n\n---`
    : '';

  const comicBlock = comicUrl
    ? `\n\n---\n![${bundle.comicStrip?.title || 'Comic Strip'}: ${bundle.trendName}](${comicUrl})\n*🎭 ${bundle.comicStrip?.concept || 'A visual story — ' + bundle.trendName}*\n\n---`
    : '';

  const insertComicAt = Math.min(paragraphs.length - 1, Math.floor(paragraphs.length * 0.75));
  const parts = [
    paragraphs.slice(0, splitAt).join('\n\n'),
    infographicBlock,
    paragraphs.slice(splitAt, insertComicAt).join('\n\n'),
    comicBlock,
    paragraphs.slice(insertComicAt).join('\n\n'),
  ].filter(Boolean);

  return parts.join('\n\n');
}

const AGENT_STEPS = [
  { label: 'Scout Agent — scanning trend signals', icon: BarChart2, pct: 10 },
  { label: 'ContentGen Agent — writing blog post', icon: FileText, pct: 30 },
  { label: 'VisualForge Agent — building infographic', icon: Image, pct: 60 },
  { label: 'ComicGen Agent — writing comic strip', icon: BookOpen, pct: 76 },
  { label: 'VideoReel Agent — scripting reel', icon: Video, pct: 88 },
  { label: 'Finalising content bundle…', icon: Sparkles, pct: 96 },
];

// ── Canva deep-link builders ──────────────────────────────────────────────
// Note: Canva's full autofill API requires Enterprise. These smart deep-links
// open the right Canva template gallery pre-searched with the trend topic —
// the clipboard copy puts all content in a structured format for fast paste.

function buildCanvaInfographicUrl(bundle: GeneratedBundle): string {
  const q = encodeURIComponent((bundle.infographic?.title || bundle.trendName).split(':')[0].trim());
  return `https://www.canva.com/create/infographics/?query=${q}`;
}

function buildCanvaComicUrl(bundle: GeneratedBundle): string {
  const q = encodeURIComponent(bundle.trendName.split(':')[0].trim());
  return `https://www.canva.com/create/comic-strips/?query=${q}`;
}

function buildCanvaReelUrl(bundle: GeneratedBundle): string {
  const q = encodeURIComponent((bundle.reelScript?.title || bundle.trendName).split(':')[0].trim());
  return `https://www.canva.com/create/reels/?query=${q}`;
}

// ── Clipboard copy builders ────────────────────────────────────────────────

function buildInfographicBrief(bundle: GeneratedBundle): string {
  const info = bundle.infographic;
  return [
    '━━━ INFOGRAPHIC BRIEF ━━━',
    `📌 Title: ${info.title}`,
    `📝 Subtitle: ${info.subtitle}`,
    '',
    '📊 STAT HIGHLIGHTS (use as big number blocks):',
    ...(info.statHighlights?.map((s) => `   [STAT] ${s.value}  |  [LABEL] ${s.label}`) || []),
    '',
    '🔢 DATA POINTS (use as numbered list):',
    ...(info.dataPoints?.map((d, i) => `   ${i + 1}. ${d}`) || []),
    '',
    `📐 Suggested Layout: ${info.suggestedLayout}`,
    `🎨 Color Scheme: ${info.colorScheme}`,
    `🏷  Keywords: ${bundle.trendKeywords?.join(', ')}`,
    '',
    '━━━ PASTE EACH VALUE INTO THE MATCHING CANVA TEXT BOX ━━━',
  ].join('\n');
}

function buildVeedScript(bundle: GeneratedBundle): string {
  const r = bundle.reelScript;
  if (!r) return '';
  return [
    `━━━ VEED.IO REEL SCRIPT ━━━`,
    `Title: ${r.title}`,
    `Duration: ${r.duration}`,
    '',
    `🎣 HOOK (0:00–0:03) — record this first`,
    r.hook,
    '',
    '━━━ SCENES ━━━',
    ...(r.scenes?.map((s) =>
      `⏱ ${s.timecode}\n  [ON SCREEN] ${s.visual}\n  [SAY THIS]  ${s.voiceover}`
    ) || []),
    '',
    `📣 CALL TO ACTION: ${r.callToAction}`,
    '',
    `HASHTAGS: ${r.hashtags?.join(' ')}`,
    '',
    '━━━ HOW TO USE IN VEED.IO ━━━',
    '1. Go to veed.io → New Project → Start from Script',
    '2. Paste this script into the Script Editor',
    '3. Auto-generates slides — each scene = one clip',
    '4. Add AI Avatar (HeyGen-style) or record your own voice',
    '5. Export as MP4 (1080x1920 for Reels/TikTok)',
  ].join('\n');
}

function buildHeyGenScript(bundle: GeneratedBundle): string {
  const r = bundle.reelScript;
  if (!r) return '';
  return [
    `━━━ HEYGEN AI AVATAR SCRIPT ━━━`,
    `Title: ${r.title}  |  Duration: ${r.duration}`,
    '',
    `🎣 HOOK: ${r.hook}`,
    '',
    ...( r.scenes?.map((s) =>
      `[${s.timecode}] ${s.visual}\n         AVATAR SAYS: ${s.voiceover}`
    ) || []),
    '',
    `CTA: ${r.callToAction}`,
    `HASHTAGS: ${r.hashtags?.join(' ')}`,
    '',
    '━━━ HOW TO USE IN HEYGEN ━━━',
    '1. Go to heygen.com → Create Video → AI Avatar',
    '2. Select your avatar and language/voice',
    '3. Paste each scene\'s AVATAR SAYS text into the script box',
    '4. Add background slides matching the [ON SCREEN] visuals',
    '5. Generate → download MP4 → publish to LinkedIn / IG / YouTube',
  ].join('\n');
}

function buildComicBrief(bundle: GeneratedBundle): string {
  if (!bundle.comicStrip) return '';
  const c = bundle.comicStrip;
  return [
    `━━━ COMIC STRIP BRIEF ━━━`,
    `📖 Title: ${c.title}`,
    `💡 Concept: ${c.concept}`,
    `🎨 Style: ${c.style}`,
    '',
    ...( c.panels?.map((p) => [
      `┌── PANEL ${p.panel} ${p.emoji} ──────────────────`,
      `│ Setting:   ${p.setting}`,
      `│ Characters: ${p.characters}`,
      `│ Action:    ${p.action}`,
      `│ Caption:   ${p.caption}`,
      `│ 💬 Dialogue: "${p.dialogue}"`,
      `└────────────────────────────────`,
    ].join('\n')) || []),
    '',
    `🌟 Moral: ${c.moralOfStory}`,
    '',
    '━━━ HOW TO USE IN CANVA ━━━',
    '1. Click "Make in Canva" → pick a 6-panel comic template',
    '2. For each panel: set background to match Setting',
    '3. Add character emojis/stickers for Characters',
    '4. Paste Caption as the narrator box (top or bottom)',
    '5. Paste Dialogue into the speech bubble text box',
    '6. Or: paste each panel into ChatGPT + DALL·E to generate panel images',
  ].join('\n');
}

// ── Free AI Video Tool URL builders ─────────────────────────────────────────

function buildLumaDreamUrl(bundle: GeneratedBundle): string {
  // Luma AI Dream Machine — text-to-video, free tier
  // Pre-fill the prompt textarea via URL hash
  const hook = bundle.reelScript?.hook || bundle.trendName;
  const scene1 = bundle.reelScript?.scenes?.[0]?.visual || '';
  const prompt = encodeURIComponent(`${hook} — ${scene1}. Cinematic, 9:16 vertical video, smooth motion, vibrant colors.`);
  return `https://lumalabs.ai/dream-machine?prompt=${prompt}`;
}

function buildPikaUrl(bundle: GeneratedBundle): string {
  // Pika Labs — free text-to-video
  return `https://pika.art/create`;
}

function buildRunwayUrl(bundle: GeneratedBundle): string {
  // Runway ML Gen-3 — 5 free credits/month
  const _bundle = bundle;
  void _bundle;
  return `https://runwayml.com/ai-magic-tools/gen-3-alpha/`;
}

function buildElevenLabsUrl(bundle: GeneratedBundle): string {
  // ElevenLabs free TTS — 10,000 chars/month for voiceover
  const hook = encodeURIComponent(bundle.reelScript?.hook || bundle.trendName || '');
  return `https://elevenlabs.io/text-to-speech?text=${hook}`;
}

const FREE_VIDEO_TOOLS: Array<{
  name: string;
  emoji: string;
  label: string;
  badge: string;
  badgeColor: string;
  desc: string;
  getUrl: (b: GeneratedBundle) => string;
}> = [
  {
    name: 'VEED.io',
    emoji: '🎬',
    label: 'AI Video Editor',
    badge: 'Free tier',
    badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    desc: 'Script-to-video AI editor. Paste your reel script, add subtitles, and export in 1080p. Works in-browser.',
    getUrl: () => 'https://www.veed.io/tools/ai-video',
  },
  {
    name: 'HeyGen',
    emoji: '🧑‍💼',
    label: 'AI Avatar + Translation',
    badge: '1 min free/mo',
    badgeColor: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
    desc: 'Create a talking AI avatar video from your script. Ideal for LinkedIn thought leadership reels.',
    getUrl: () => 'https://www.heygen.com',
  },
  {
    name: 'Pika Labs',
    emoji: '⚡',
    label: 'Image → Video',
    badge: 'Free',
    badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    desc: 'Animate your infographic or a still image into a motion clip. Great for carousel-to-video.',
    getUrl: buildPikaUrl,
  },
  {
    name: 'Runway Gen-3',
    emoji: '🚀',
    label: 'Text → Cinematic Video',
    badge: '5 free credits',
    badgeColor: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
    desc: 'Hollywood-grade AI video generation. Use your hook as the prompt for a stunning intro clip.',
    getUrl: buildRunwayUrl,
  },
  {
    name: 'ElevenLabs TTS',
    emoji: '🎙️',
    label: 'AI Voiceover',
    badge: '10k chars free',
    badgeColor: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30',
    desc: 'Paste the reel voiceover script and download a natural AI voice narration (120+ voices).',
    getUrl: buildElevenLabsUrl,
  },
];

const ContentPipeline: React.FC = () => {
  // Load trends from the last domain session, fall back to mock UX trends
  const session = getLastSession();
  const sessionTrends: DiscoveredTrend[] = session?.trends ?? [];
  const selectedIds: string[] = session?.selectedTrendIds ?? [];

  // Prefer the user-selected trends from discovery; if none selected, show all session trends
  const domainTrends = selectedIds.length > 0
    ? sessionTrends.filter((t) => selectedIds.includes(t.id))
    : sessionTrends;

  // Use domain trends if available, otherwise fall back to mock UX data
  const availableTrends = domainTrends.length > 0 ? domainTrends : (mockTrends as unknown as DiscoveredTrend[]);
  const domainLabel = session?.domain?.label ?? 'UX Design';

  const [selectedTrendId, setSelectedTrendId] = useState(availableTrends[0]?.id || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [bundles, setBundles] = useState<GeneratedBundle[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('blog');
  const [copied, setCopied] = useState(false);
  const [teleprompter, setTeleprompter] = useState(false);
  const [veedCopied, setVeedCopied] = useState(false);
  const [briefCopied, setBriefCopied] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  // Gemini image generation state — keyed by bundle id
  type GeminiImgState = { loading: boolean; imageUrl: string | null; imageBase64: string | null; error: string | null };
  const [geminiState, setGeminiState] = useState<Record<string, GeminiImgState>>({});
  const [comicGeminiState, setComicGeminiState] = useState<Record<string, GeminiImgState>>({});
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const geminiKeyRef = useRef<string>('');

  useEffect(() => {
    setBundles(getStoredBundles());
    setHasApiKey(true); // Together AI is always available — no key required
    // Load Gemini key from localStorage
    geminiKeyRef.current = localStorage.getItem('uxcf_gemini_key') || (import.meta.env.VITE_GOOGLE_AI_API_KEY as string) || '';
  }, []);

  const handleGenerateInfographic = async (bundle: GeneratedBundle) => {
    // v3: Fetch a reference photo from Pollinations (realism model) as visual
    // background, then Canvas overlays all data on top for a magazine-quality look.
    setGeminiState((prev) => ({ ...prev, [bundle.id]: { loading: true, imageUrl: null, imageBase64: null, error: null } }));
    try {
      const sbUrl = (import.meta.env.VITE_SUPABASE_URL as string) || '';
      const sbKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || '';

      // Step 1: Fetch a reference background photo from Pollinations (non-blocking)
      let bgImageSrc = '';
      try {
        const bgPrompt = encodeURIComponent(
          `professional editorial photo: ${bundle.trendName}, modern office, data visualization, ` +
          `tech workspace, bokeh background, muted blue purple tones, no text, no words, photorealistic`
        );
        const bgSeed = Math.floor(Math.random() * 999999);
        const bgUrl = `https://image.pollinations.ai/prompt/${bgPrompt}?width=1080&height=400&model=flux-realism&nologo=true&seed=${bgSeed}&nofeed=true`;
        // Use URL directly — loadImage in compositor handles cross-origin
        bgImageSrc = bgUrl;
      } catch { /* ignore — canvas renders fine without bg */ }

      // Step 2: Canvas render with reference bg
      const finalBase64 = await compositeInfographic(bgImageSrc, bundle.infographic, bundle.trendName);

      // Step 2: Upload composited PNG to Supabase Storage
      let publicUrl: string | null = null;
      try {
        const uploadRes = await fetch(`${sbUrl}/functions/v1/upload-composited-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'apikey': sbKey, 'Authorization': `Bearer ${sbKey}` },
          body: JSON.stringify({
            dataUrl: finalBase64,
            prefix: 'infographic',
            slug: bundle.trendName,
          }),
        });
        const uploadData = await uploadRes.json() as { publicUrl?: string; error?: string };
        if (uploadData.publicUrl) publicUrl = uploadData.publicUrl;
      } catch (uploadErr) {
        console.warn('Upload to storage failed, will use in-memory only:', uploadErr);
      }

      // Use public URL if available, else fall back to base64 in memory (not persisted)
      const imageToStore = publicUrl || finalBase64;
      setGeminiState((prev) => ({
        ...prev,
        [bundle.id]: { loading: false, imageUrl: publicUrl, imageBase64: publicUrl ? null : finalBase64, error: null },
      }));

      // Persist only the URL (tiny string — no quota issues)
      try {
        const stored = JSON.parse(localStorage.getItem('uxcf_infographic_urls') || '{}');
        stored[bundle.id] = imageToStore.startsWith('http') ? imageToStore : null;
        localStorage.setItem('uxcf_infographic_urls', JSON.stringify(stored));
      } catch { /* ignore quota errors for URL persistence */ }
    } catch (e) {
      setGeminiState((prev) => ({ ...prev, [bundle.id]: { loading: false, imageUrl: null, imageBase64: null, error: String(e) } }));
    }
  };

  const handleGenerateComic = async (bundle: GeneratedBundle) => {
    // v5: Panels fetched SEQUENTIALLY (staggered 700ms) to avoid Pollinations
    // rate-limiting. loadImage in compositor also retries 3× — so every panel
    // gets a real image. Canvas compositor draws the 2×3 grid.
    setComicGeminiState((prev) => ({ ...prev, [bundle.id]: { loading: true, imageUrl: null, imageBase64: null, error: null } }));
    try {
      if (!bundle.comicStrip) throw new Error('No comic strip data available.');
      const sbUrl2 = (import.meta.env.VITE_SUPABASE_URL as string) || '';
      const sbKey2 = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || '';
      const styleNote = bundle.comicStrip.style || 'bright cartoonish art with exaggerated expressions';
      const panels = bundle.comicStrip.panels?.slice(0, 6) || [];

      // ── Build a Pollinations URL for one panel ────────────────────────────
      const buildPanelUrl = (p: typeof panels[0], idx: number): string => {
        const prompt = [
          `single comic panel: ${p.characters}`,
          `action: ${p.action}`,
          `setting: ${p.setting}`,
          `topic: "${bundle.trendName}"`,
          `style: ${styleNote}`,
          'comic book illustration, bold outlines, expressive faces',
          'vibrant colors, purple teal amber palette, navy background',
          'NO text, NO words, NO speech bubbles, NO captions',
        ].join(', ');
        const seed = 42 + idx * 137; // deterministic unique seed per panel
        return `https://image.pollinations.ai/prompt/${encodeURIComponent(prompt)}?width=540&height=540&model=flux&nologo=true&seed=${seed}&nofeed=true`;
      };

      // ── Fetch panels SEQUENTIALLY into base64, 900ms gap ─────────────────
      // Pollinations drops concurrent requests — sequential + retry ensures
      // every panel gets a real image before we call the Canvas compositor.
      const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

      // Use fetch() → blob → object URL so the image is never CORS-tainted on the canvas
      const fetchPanelAsBase64 = async (url: string): Promise<string | null> => {
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const fetchUrl = url + (attempt > 0 ? `&_r=${attempt}` : '');
            const resp = await fetch(fetchUrl, { mode: 'cors', cache: 'no-cache' });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            return await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = () => reject(new Error('FileReader failed'));
              reader.readAsDataURL(blob);
            });
          } catch {
            if (attempt < 2) await sleep(1800 * (attempt + 1));
          }
        }
        return null; // compositor uses gradient fallback for this panel
      };

      const resolvedPanels: (string | null)[] = [];
      for (let i = 0; i < panels.length; i++) {
        const b64 = await fetchPanelAsBase64(buildPanelUrl(panels[i], i));
        resolvedPanels.push(b64);
        if (i < panels.length - 1) await sleep(900); // 900ms between requests
      }

      // Step 1: Composite with fully resolved base64 panel images
      const finalBase642 = await compositeComic(resolvedPanels, bundle.comicStrip);

      // Step 2: Upload composited PNG to Supabase Storage
      let publicUrl2: string | null = null;
      try {
        const uploadRes2 = await fetch(`${sbUrl2}/functions/v1/upload-composited-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'apikey': sbKey2, 'Authorization': `Bearer ${sbKey2}` },
          body: JSON.stringify({ dataUrl: finalBase642, prefix: 'comic', slug: bundle.trendName }),
        });
        const uploadData2 = await uploadRes2.json() as { publicUrl?: string; error?: string };
        if (uploadData2.publicUrl) publicUrl2 = uploadData2.publicUrl;
      } catch (uploadErr2) {
        console.warn('Upload to storage failed for comic, using in-memory only:', uploadErr2);
      }

      const imageToStore2 = publicUrl2 || finalBase642;
      setComicGeminiState((prev) => ({
        ...prev,
        [bundle.id]: { loading: false, imageUrl: publicUrl2, imageBase64: publicUrl2 ? null : finalBase642, error: null },
      }));
      try {
        const stored2 = JSON.parse(localStorage.getItem('uxcf_comic_urls') || '{}');
        stored2[bundle.id] = imageToStore2.startsWith('http') ? imageToStore2 : null;
        localStorage.setItem('uxcf_comic_urls', JSON.stringify(stored2));
      } catch { /* ignore */ }
    } catch (e) {
      setComicGeminiState((prev) => ({ ...prev, [bundle.id]: { loading: false, imageUrl: null, imageBase64: null, error: String(e) } }));
    }
  };

  // Restore previously generated images from localStorage on mount.
  // Only public URLs are stored now — no large base64 blobs.
  useEffect(() => {
    // Clear any legacy base64 blobs that caused QuotaExceededError
    try {
      localStorage.removeItem('uxcf_infographic_images');
      localStorage.removeItem('uxcf_comic_images');
    } catch { /* ignore */ }

    try {
      const stored = JSON.parse(localStorage.getItem('uxcf_infographic_urls') || '{}');
      const restored: Record<string, GeminiImgState> = {};
      for (const [id, url] of Object.entries(stored)) {
        if (url && typeof url === 'string') {
          restored[id] = { loading: false, imageUrl: url, imageBase64: null, error: null };
        }
      }
      if (Object.keys(restored).length > 0) setGeminiState(restored);
    } catch { /* ignore */ }

    try {
      const storedComic = JSON.parse(localStorage.getItem('uxcf_comic_urls') || '{}');
      const restoredComic: Record<string, GeminiImgState> = {};
      for (const [id, url] of Object.entries(storedComic)) {
        if (url && typeof url === 'string') {
          restoredComic[id] = { loading: false, imageUrl: url, imageBase64: null, error: null };
        }
      }
      if (Object.keys(restoredComic).length > 0) setComicGeminiState(restoredComic);
    } catch { /* ignore */ }
  }, []);

  const handleGenerate = async (overrideTrendId?: string, tonality: string = 'Professional / Authoritative', voiceStyle: string = 'Executive') => {
    const effectiveTrendId = overrideTrendId || selectedTrendId;
    const trend = availableTrends.find((t) => t.id === effectiveTrendId) || availableTrends[0];
    if (!trend) return;
    // No key check needed — Together AI is always available as free fallback

    setSelectedTrendId(effectiveTrendId);
    setIsGenerating(true);
    setError(null);
    setProgress(0);
    setProgressLabel('Initialising agents…');

    try {
      const brandProfile = getStoredBrandProfile() || undefined;
      const bundle = await generateContentBundle(
        '', // key param kept for compat; actual keys loaded from localStorage
        {
          id: trend.id,
          name: trend.name,
          description: trend.description,
          keywords: trend.keywords,
          domain: domainLabel,
          tonality,
          voiceStyle,
          brandProfile,
        },
        (step, pct) => {
          setProgressLabel(step);
          setProgress(pct);
        }
      );
      setBundles(getStoredBundles());
      setExpandedId(bundle.id);
      setActiveTab('blog');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Generation failed.');
    } finally {
      setIsGenerating(false);
      setProgress(0);
    }
  };

  const handleApprove = (id: string) => {
    updateBundleApproval(id, 'approved');
    setBundles(getStoredBundles());
  };

  const handleReject = (id: string) => {
    updateBundleApproval(id, 'rejected');
    setBundles(getStoredBundles());
  };

  const handleDelete = (id: string) => {
    deleteBundle(id);
    setBundles(getStoredBundles());
    if (expandedId === id) setExpandedId(null);
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('Clipboard API failed, using fallback', err);
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <Layout>
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-6xl mx-auto">

        {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-violet-400" />
              Content Pipeline
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Domain: <span className="text-violet-300 font-medium">{domainLabel}</span> · Multi-agent AI generation
            </p>
          </div>
          {!hasApiKey && (
            <a
              href="#/settings"
              onClick={() => window.location.hash = '/settings'}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm hover:bg-amber-500/20 transition-colors"
            >
              <Key className="w-4 h-4" />
              Add OpenAI Key in Settings
            </a>
          )}
        </div>

        {/* Typeform-style Generator */}
        <TypeformPipeline
          trends={availableTrends}
          domainLabel={domainLabel}
          hasApiKey={hasApiKey}
          isGenerating={isGenerating}
          progress={progress}
          progressLabel={progressLabel}
          onGenerate={(trendId, tonality, voiceStyle) => {
            setSelectedTrendId(trendId);
            handleGenerate(trendId, tonality, voiceStyle);
          }}
        />

        {/* Error banner */}
        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        {/* Bundles List */}
        {bundles.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
              Generated Bundles ({bundles.length})
            </h2>

            {bundles.map((bundle) => {
              const isExpanded = expandedId === bundle.id;
              const statusColor =
                bundle.approvalStatus === 'approved' ? 'text-green-400 bg-green-500/10 border-green-500/30' :
                bundle.approvalStatus === 'rejected' ? 'text-red-400 bg-red-500/10 border-red-500/30' :
                'text-amber-400 bg-amber-500/10 border-amber-500/30';

              return (
                <div key={bundle.id} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                  {/* Bundle Header */}
                  <div className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Sparkles className="w-5 h-5 text-violet-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-slate-100 font-medium truncate">{bundle.trendName}</p>
                        <p className="text-slate-500 text-xs">
                          {new Date(bundle.generatedAt).toLocaleString()} · {bundle.blogPost?.wordCount?.toLocaleString()} words
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded border ${statusColor}`}>
                        {bundle.approvalStatus}
                      </span>
                      {bundle.approvalStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(bundle.id)}
                            className="p-1.5 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-400 transition-colors"
                            title="Approve"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleReject(bundle.id)}
                            className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                            title="Reject"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(bundle.id)}
                        className="p-1.5 rounded-lg bg-slate-800 hover:bg-red-500/10 hover:text-red-400 text-slate-500 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : bundle.id)}
                        className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 transition-colors"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-slate-800">
                      {/* Tabs */}
                      <div className="flex border-b border-slate-800 px-3 sm:px-5 overflow-x-auto scrollbar-hide gap-0">
                        {(['blog', 'newsletter', 'infographic', 'reel', 'comic', 'social', 'visual', 'video', 'whitepaper'] as TabType[]).map((tab) => {
                          const icons: Record<TabType, React.ElementType> = {
                            blog: FileText, newsletter: Rss, infographic: Image, reel: Video, comic: BookOpen,
                            social: Share2, visual: Layers, video: Film, whitepaper: FileSearch2,
                          };
                          const labels: Record<TabType, string> = {
                            blog: 'Blog', newsletter: 'Newsletter', infographic: 'Infographic', reel: 'Reel',
                            comic: 'Comic', social: 'Social Assets', visual: 'Visual Factory', video: 'Video Workflow', whitepaper: 'Whitepaper',
                          };
                          const Icon = icons[tab];
                          return (
                            <button
                              key={tab}
                              onClick={() => setActiveTab(tab)}
                              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                                activeTab === tab
                                  ? 'border-violet-500 text-violet-400'
                                  : 'border-transparent text-slate-500 hover:text-slate-300'
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                              {labels[tab]}
                            </button>
                          );
                        })}
                      </div>

                      <div className="p-3 sm:p-5">
                        {/* Blog Tab */}
                        {activeTab === 'blog' && bundle.blogPost && (() => {
                          const gsInfographic = geminiState[bundle.id];
                          const gsComi = comicGeminiState[bundle.id];
                          const infographicUrl = gsInfographic?.imageUrl || gsInfographic?.imageBase64 || null;
                          const comicUrl = gsComi?.imageUrl || gsComi?.imageBase64 || null;
                          const hasVisuals = !!(infographicUrl || comicUrl);
                          const enrichedBody = hasVisuals ? buildEnrichedBlogBody(bundle, infographicUrl, comicUrl) : null;
                          return (
                            <div className="space-y-4">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <h3 className="text-slate-100 font-semibold text-lg">{bundle.blogPost.title}</h3>
                                  <p className="text-slate-400 text-sm mt-1">{bundle.blogPost.excerpt}</p>
                                </div>
                                <div className="flex gap-2 flex-shrink-0">
                                  <button
                                    onClick={() => {
                                      const tags = bundle.blogPost.tags || [];
                                      const hashtags = tags.map(t => `#${t.replace(/\s+/g, '')}`).join(' ');
                                      const body = bundle.blogPost.body;
                                      const finalBody = (hashtags && !body.includes(hashtags)) ? `${body}\n\n${hashtags}` : body;
                                      handleCopy(bundle.blogPost.title + '\n\n' + finalBody);
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400 text-xs transition-colors"
                                  >
                                    {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                                    {copied ? 'Copied!' : 'Copy Plain'}
                                  </button>
                                </div>
                              </div>
                              <div className="flex gap-4 text-xs">
                                <span className="text-cyan-400">{bundle.blogPost.wordCount?.toLocaleString()} words</span>
                                <span className="text-slate-500">·</span>
                                <span className="text-slate-400">{bundle.blogPost.readTime}</span>
                                <span className="text-slate-500">·</span>
                                <span className="text-green-400">SEO {bundle.blogPost.seoScore}/100</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {bundle.blogPost.tags?.map((tag) => (
                                  <span key={tag} className="px-2 py-0.5 bg-slate-800 rounded text-slate-400 text-xs">#{tag}</span>
                                ))}
                              </div>
                              <div className="bg-slate-800/60 rounded-lg p-4 max-h-72 overflow-y-auto">
                                <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans leading-relaxed">
                                  {bundle.blogPost.body}
                                </pre>
                              </div>

                              {/* ── Embed Visuals into Post ──────────────────────── */}
                              <div className={`rounded-xl border overflow-hidden ${
                                hasVisuals
                                  ? 'border-violet-500/30 bg-gradient-to-br from-violet-900/20 to-cyan-900/10'
                                  : 'border-slate-700/50 bg-slate-800/30'
                              }`}>
                                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/8">
                                  <div className="flex items-center gap-2">
                                    <Wand2 className={`w-4 h-4 ${hasVisuals ? 'text-violet-400' : 'text-slate-500'}`} />
                                    <span className={`text-sm font-semibold ${hasVisuals ? 'text-white' : 'text-slate-400'}`}>
                                      Embed Visuals into Post
                                    </span>
                                  </div>
                                  {hasVisuals && (
                                    <div className="flex gap-1.5 ml-1">
                                      {infographicUrl && (
                                        <span className="px-2 py-0.5 rounded-full bg-violet-500/20 border border-violet-500/30 text-violet-300 text-[10px] font-medium">📊 Infographic ready</span>
                                      )}
                                      {comicUrl && (
                                        <span className="px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-300 text-[10px] font-medium">🎭 Comic ready</span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {!hasVisuals ? (
                                  <div className="px-4 py-5 text-center">
                                    <p className="text-slate-500 text-sm">No visuals generated yet.</p>
                                    <p className="text-slate-600 text-xs mt-1">Generate an infographic or comic strip first (from the tabs above), then come back here to embed them.</p>
                                    <div className="flex gap-2 justify-center mt-3">
                                      <button
                                        onClick={() => setActiveTab('infographic')}
                                        className="px-3 py-1.5 rounded-lg bg-violet-600/20 border border-violet-500/30 text-violet-300 text-xs hover:bg-violet-600/30 transition-colors"
                                      >
                                        → Go to Infographic tab
                                      </button>
                                      <button
                                        onClick={() => setActiveTab('comic')}
                                        className="px-3 py-1.5 rounded-lg bg-amber-600/20 border border-amber-500/30 text-amber-300 text-xs hover:bg-amber-600/30 transition-colors"
                                      >
                                        → Go to Comic Strip tab
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-4 space-y-4">
                                    {/* Thumbnail previews */}
                                    <div className="flex gap-3">
                                      {infographicUrl && (
                                        <div className="flex items-center gap-2 flex-1 min-w-0 bg-slate-800/60 rounded-lg p-2">
                                          <img src={infographicUrl} alt="Infographic" className="w-12 h-16 object-cover rounded flex-shrink-0 bg-slate-700" />
                                          <div className="min-w-0">
                                            <p className="text-xs font-medium text-violet-300 truncate">{bundle.infographic?.title || 'Infographic'}</p>
                                            <p className="text-[10px] text-slate-500">Will be inserted at ~40% of post</p>
                                          </div>
                                        </div>
                                      )}
                                      {comicUrl && (
                                        <div className="flex items-center gap-2 flex-1 min-w-0 bg-slate-800/60 rounded-lg p-2">
                                          <img src={comicUrl} alt="Comic Strip" className="w-12 h-16 object-cover rounded flex-shrink-0 bg-slate-700" />
                                          <div className="min-w-0">
                                            <p className="text-xs font-medium text-amber-300 truncate">{bundle.comicStrip?.title || 'Comic Strip'}</p>
                                            <p className="text-[10px] text-slate-500">Will be inserted at ~75% of post</p>
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Enriched body preview */}
                                    {enrichedBody && (
                                      <div>
                                        <div className="flex items-center justify-between mb-2">
                                          <p className="text-xs text-slate-400 font-medium">📄 Enriched Post Preview <span className="text-slate-600">(Markdown)</span></p>
                                          <button
                                            onClick={() => handleCopy(bundle.blogPost.title + '\n\n' + enrichedBody)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 rounded-lg text-white text-xs font-medium transition-colors"
                                          >
                                            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                            {copied ? 'Copied!' : 'Copy Enriched Post'}
                                          </button>
                                        </div>
                                        <div className="bg-slate-900/80 rounded-lg p-4 max-h-64 overflow-y-auto border border-white/8">
                                          <pre className="text-slate-300 text-xs whitespace-pre-wrap font-mono leading-relaxed">
                                            {bundle.blogPost.title + '\n\n' + enrichedBody}
                                          </pre>
                                        </div>
                                        <p className="text-[10px] text-slate-600 mt-2">💡 Image URLs use Markdown syntax — paste this into WordPress, Ghost, Hashnode, or any Markdown CMS to auto-embed the visuals.</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Infographic Tab */}
                        {activeTab === 'infographic' && bundle.infographic && (() => {
                          const gs = geminiState[bundle.id];
                          const generatedImg = gs?.imageUrl || gs?.imageBase64 || null;
                          return (
                            <div className="space-y-4">
                              {/* Header row */}
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <h3 className="text-slate-100 font-semibold text-lg">{bundle.infographic.title}</h3>
                                  <p className="text-slate-400 text-sm">{bundle.infographic.subtitle}</p>
                                </div>
                                <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
                                  <button
                                    onClick={() => { navigator.clipboard.writeText(buildInfographicBrief(bundle)); setBriefCopied(true); setTimeout(() => setBriefCopied(false), 2000); }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 text-xs transition-colors"
                                  >
                                    {briefCopied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                                    {briefCopied ? 'Copied!' : 'Copy Brief'}
                                  </button>
                                  <a href={buildCanvaInfographicUrl(bundle)} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-xs font-medium transition-colors">
                                    <ExternalLink className="w-3.5 h-3.5" /> Open in Canva
                                  </a>
                                  {/* AI generate button */}
                                  <button
                                    onClick={() => handleGenerateInfographic(bundle)}
                                    disabled={gs?.loading}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 disabled:opacity-60 rounded-lg text-white text-xs font-semibold transition-all shadow-lg shadow-violet-500/20"
                                  >
                                    {gs?.loading
                                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>
                                      : <><Wand2 className="w-3.5 h-3.5" /> {generatedImg ? 'Regenerate Image' : 'Generate with AI'}</>
                                    }
                                  </button>
                                </div>
                              </div>

                              {/* AI image error */}
                              {gs?.error && (
                                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs text-red-300">
                                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                  <span>{gs.error}</span>
                                </div>
                              )}

                              {/* AI image loading skeleton */}
                              {gs?.loading && (
                                <div className="relative bg-slate-800 rounded-xl overflow-hidden" style={{aspectRatio:'9/16', maxHeight: 500}}>
                                  <div className="absolute inset-0 bg-gradient-to-b from-violet-600/10 via-cyan-600/5 to-violet-600/10 animate-pulse" />
                                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                                    <Wand2 className="w-8 h-8 text-violet-400 animate-bounce" />
                                    <p className="text-violet-300 text-sm font-medium">Canvas is rendering your infographic…</p>
                                    <p className="text-slate-500 text-xs">Rendering in your browser — usually instant</p>
                                  </div>
                                </div>
                              )}

                              {/* Generated image */}
                              {generatedImg && !gs?.loading && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <p className="text-xs text-green-400 font-medium flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Canvas Rendered (no API needed)</p>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => setLightboxSrc(generatedImg)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 text-xs transition-colors"
                                      >
                                        <ZoomIn className="w-3.5 h-3.5" /> Full Size
                                      </button>
                                      <a
                                        href={generatedImg}
                                        download={`infographic-${bundle.trendName.replace(/\s+/g,'-')}.png`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 rounded-lg text-white text-xs font-medium transition-colors"
                                      >
                                        <Download className="w-3.5 h-3.5" /> Download
                                      </a>
                                    </div>
                                  </div>
                                  <div
                                    className="relative rounded-xl overflow-hidden border border-violet-500/20 cursor-zoom-in"
                                    onClick={() => setLightboxSrc(generatedImg)}
                                  >
                                    <img
                                      src={generatedImg}
                                      alt={bundle.infographic.title}
                                      className="w-full object-contain max-h-[600px] bg-slate-900"
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Stat highlights */}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {bundle.infographic.statHighlights?.map((stat, i) => (
                                  <div key={i} className="bg-gradient-to-br from-violet-600/20 to-cyan-600/20 border border-violet-500/20 rounded-xl p-4 text-center">
                                    <p className="text-2xl font-bold text-violet-300">{stat.value}</p>
                                    <p className="text-slate-400 text-xs mt-1">{stat.label}</p>
                                  </div>
                                ))}
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Data Points</p>
                                <ul className="space-y-2">
                                  {bundle.infographic.dataPoints?.map((pt, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                      <span className="w-5 h-5 rounded-full bg-violet-600/30 text-violet-300 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                                      {pt}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="bg-slate-800/50 rounded-lg p-3 text-sm text-slate-400">
                                <span className="text-slate-500">Layout: </span>{bundle.infographic.suggestedLayout}
                                <span className="ml-4 text-slate-500">Colors: </span><span className="text-cyan-400">{bundle.infographic.colorScheme}</span>
                              </div>
                              <div className="flex items-start gap-2 bg-violet-500/10 border border-violet-500/20 rounded-lg p-3 text-xs text-violet-300">
                                <span>✨</span>
                                <span><strong>Canvas Renderer v2:</strong> Click <em>Generate with AI</em> to render a properly designed infographic in your browser — gradient background, hero stat, stat cards, bar chart, and bullet insights. Completely free, instant, no API key needed. Download as PNG or refine in Canva.</span>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Reel Tab */}
                        {activeTab === 'reel' && bundle.reelScript && (
                          <div className="space-y-4">
                            {/* Teleprompter fullscreen overlay */}
                            {teleprompter && (
                              <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-start pt-12 px-8 overflow-y-auto">
                                <div className="max-w-2xl w-full">
                                  <div className="flex items-center justify-between mb-8">
                                    <span className="text-green-400 font-mono text-sm tracking-widest">● TELEPROMPTER</span>
                                    <button onClick={() => setTeleprompter(false)} className="text-white/60 hover:text-white"><X className="w-6 h-6" /></button>
                                  </div>
                                  <p className="text-green-300 text-3xl font-bold text-center leading-relaxed mb-10">{bundle.reelScript.hook}</p>
                                  {bundle.reelScript.scenes?.map((scene, i) => (
                                    <div key={i} className="mb-10 text-center border-b border-white/10 pb-8">
                                      <span className="text-green-600 text-xs font-mono block mb-3">{scene.timecode}</span>
                                      <p className="text-white text-2xl leading-loose">{scene.voiceover}</p>
                                    </div>
                                  ))}
                                  <p className="text-green-400 text-2xl text-center font-bold mt-6">{bundle.reelScript.callToAction}</p>
                                </div>
                              </div>
                            )}
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h3 className="text-slate-100 font-semibold text-lg">{bundle.reelScript.title}</h3>
                                <p className="text-cyan-400 text-sm mt-1">🎣 Hook: {bundle.reelScript.hook}</p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="px-2 py-1 bg-slate-800 rounded text-slate-400 text-xs">{bundle.reelScript.duration}</span>
                                <button
                                  onClick={() => { navigator.clipboard.writeText(buildVeedScript(bundle)); setVeedCopied(true); setTimeout(() => setVeedCopied(false), 2000); }}
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 text-xs transition-colors"
                                >
                                  {veedCopied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Download className="w-3.5 h-3.5" />}
                                  {veedCopied ? 'Copied!' : 'Export VEED.io'}
                                </button>
                                <button
                                  onClick={() => setTeleprompter(true)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600/20 hover:bg-cyan-600/30 border border-cyan-500/30 rounded-lg text-cyan-300 text-xs transition-colors"
                                >
                                  <Maximize2 className="w-3.5 h-3.5" /> Teleprompter
                                </button>
                                <a href={buildHeyGenScript(bundle) ? 'https://www.heygen.com' : 'https://www.veed.io'} target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 rounded-lg text-white text-xs font-medium transition-colors">
                                  <ExternalLink className="w-3.5 h-3.5" /> Open HeyGen
                                </a>
                              </div>
                            </div>
                            <div className="flex items-start gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3 text-xs text-cyan-300 mb-1">
                              <span>🎬</span>
                              <span><strong>VEED.io:</strong> Click <em>Export VEED.io</em> to copy the script with timecodes and visuals → go to veed.io → New Project → Start from Script → paste. Each scene = one clip. Use <em>Teleprompter</em> to read voiceover lines while filming.</span>
                            </div>
                            <div className="flex items-start gap-2 bg-violet-500/10 border border-violet-500/20 rounded-lg p-3 text-xs text-violet-300">
                              <span>🎞️</span>
                              <span><strong>Canva Reels:</strong> Click <em>Open Reel in Canva</em> → choose a Reel (9:16) template → add text slides for each voiceover line. Great for text-based reels without filming.</span>
                            </div>

                            {/* ── Free AI Video Tools ─────────────────────────── */}
                            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-600/40 rounded-2xl p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="text-base">🤖</span>
                                <p className="text-sm font-semibold text-slate-100">Free AI Video Tools</p>
                                <span className="ml-auto px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] border border-emerald-500/30 font-semibold">ALL FREE</span>
                              </div>
                              <p className="text-xs text-slate-400 mb-3">Use your reel script + hook as prompts in any of these tools to generate or narrate your video — no subscription needed.</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {FREE_VIDEO_TOOLS.map((tool) => (
                                  <a
                                    key={tool.name}
                                    href={tool.getUrl(bundle)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-col gap-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-500 rounded-xl p-3 transition-all group"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="text-lg">{tool.emoji}</span>
                                      <span className="text-sm font-semibold text-slate-100 group-hover:text-white">{tool.name}</span>
                                      <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded border font-medium ${tool.badgeColor}`}>{tool.badge}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[10px] px-2 py-0.5 bg-slate-700 rounded text-slate-400">{tool.label}</span>
                                    </div>
                                    <p className="text-xs text-slate-400 leading-relaxed">{tool.desc}</p>
                                    <div className="flex items-center gap-1 text-[10px] text-cyan-400 group-hover:text-cyan-300 mt-0.5">
                                      <ExternalLink className="w-3 h-3" /> Open {tool.name} →
                                    </div>
                                  </a>
                                ))}
                              </div>
                              <div className="mt-3 flex items-start gap-2 bg-cyan-500/8 rounded-lg p-2.5 text-xs text-slate-400">
                                <span>💡</span>
                                <span><strong className="text-slate-300">Workflow tip:</strong> Export VEED.io script → paste scenes into ElevenLabs for voiceover → use HeyGen for AI avatar delivery → publish as LinkedIn Reel or IG video.</span>
                              </div>
                            </div>

                            <div className="space-y-2">
                              {bundle.reelScript.scenes?.map((scene, i) => (
                                <div key={i} className="grid grid-cols-12 gap-3 bg-slate-800/50 rounded-lg p-3">
                                  <div className="col-span-1 text-xs text-violet-400 font-mono mt-0.5">{scene.timecode}</div>
                                  <div className="col-span-5">
                                    <p className="text-xs text-slate-500 mb-0.5">VISUAL</p>
                                    <p className="text-sm text-slate-300">{scene.visual}</p>
                                  </div>
                                  <div className="col-span-6">
                                    <p className="text-xs text-slate-500 mb-0.5">VOICEOVER</p>
                                    <p className="text-sm text-slate-200 italic">"{scene.voiceover}"</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="bg-violet-600/10 border border-violet-500/20 rounded-lg p-3">
                              <p className="text-xs text-slate-500 mb-1">Call to Action</p>
                              <p className="text-violet-300 text-sm">{bundle.reelScript.callToAction}</p>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {bundle.reelScript.hashtags?.map((tag) => (
                                <span key={tag} className="px-2 py-0.5 bg-cyan-500/10 border border-cyan-500/20 rounded text-cyan-400 text-xs">{tag}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Comic Strip Tab */}
                        {activeTab === 'comic' && (
                          <div className="space-y-4">
                            {bundle.comicStrip ? (() => {
                              const cgs = comicGeminiState[bundle.id];
                              const comicImg = cgs?.imageUrl || cgs?.imageBase64 || null;
                              return (
                              <>
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <h3 className="text-slate-100 font-semibold text-lg">{bundle.comicStrip.title}</h3>
                                    <p className="text-slate-400 text-sm mt-1 italic">{bundle.comicStrip.concept}</p>
                                  </div>
                                  <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
                                    <button
                                      onClick={() => navigator.clipboard.writeText(buildComicBrief(bundle))}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 text-xs transition-colors"
                                    >
                                      <Copy className="w-3.5 h-3.5" /> Copy Brief
                                    </button>
                                    <a href={buildCanvaComicUrl(bundle)} target="_blank" rel="noopener noreferrer"
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-xs font-medium transition-colors">
                                      <ExternalLink className="w-3.5 h-3.5" /> Make in Canva
                                    </a>
                                    {/* AI generate button */}
                                    <button
                                      onClick={() => handleGenerateComic(bundle)}
                                      disabled={cgs?.loading}
                                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-violet-600 hover:from-amber-400 hover:to-violet-500 disabled:opacity-60 rounded-lg text-white text-xs font-semibold transition-all shadow-lg shadow-amber-500/20"
                                    >
                                      {cgs?.loading
                                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>
                                        : <><Wand2 className="w-3.5 h-3.5" /> {comicImg ? 'Regenerate Image' : 'Generate with AI'}</>
                                      }
                                    </button>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="px-2 py-1 bg-amber-500/10 border border-amber-500/20 rounded text-amber-300">🎨 {bundle.comicStrip.style}</span>
                                  <span className="text-slate-500">6 panels</span>
                                </div>

                                {/* AI image error */}
                                {cgs?.error && (
                                  <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs text-red-300">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span>{cgs.error}</span>
                                  </div>
                                )}

                                {/* AI image loading skeleton */}
                                {cgs?.loading && (
                                  <div className="relative bg-slate-800 rounded-xl overflow-hidden" style={{aspectRatio:'3/4', maxHeight: 520}}>
                                    <div className="absolute inset-0 bg-gradient-to-br from-amber-600/10 via-violet-600/5 to-amber-600/10 animate-pulse" />
                                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                                      <BookOpen className="w-8 h-8 text-amber-400 animate-bounce" />
                                      <p className="text-amber-300 text-sm font-medium">Fetching 6 FLUX panel images one-by-one…</p>
                                      <p className="text-slate-500 text-xs">Sequential fetch with auto-retry — ~45–60s for all 6 panels</p>
                                    </div>
                                  </div>
                                )}

                                {/* Generated comic image */}
                                {comicImg && !cgs?.loading && (
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                      <p className="text-xs text-green-400 font-medium flex items-center gap-1"><Check className="w-3.5 h-3.5" /> 6 unique FLUX panels + Canvas composite</p>
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => setLightboxSrc(comicImg)}
                                          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 text-xs transition-colors"
                                        >
                                          <ZoomIn className="w-3.5 h-3.5" /> Full Size
                                        </button>
                                        <a
                                          href={comicImg}
                                          download={`comic-${bundle.trendName.replace(/\s+/g, '-')}.png`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 rounded-lg text-white text-xs font-medium transition-colors"
                                        >
                                          <Download className="w-3.5 h-3.5" /> Download
                                        </a>
                                      </div>
                                    </div>
                                    <div
                                      className="relative rounded-xl overflow-hidden border border-amber-500/20 cursor-zoom-in"
                                      onClick={() => setLightboxSrc(comicImg)}
                                    >
                                      <img
                                        src={comicImg}
                                        alt={bundle.comicStrip.title}
                                        className="w-full object-contain max-h-[600px] bg-slate-900"
                                      />
                                    </div>
                                  </div>
                                )}
                                {/* Panel script cards — only shown BEFORE the AI image is generated */}
                                {!comicImg && (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {bundle.comicStrip.panels?.map((panel) => (
                                      <div key={panel.panel} className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden flex flex-col">
                                        <div className="flex items-center justify-between px-3 py-2 bg-slate-700/60 border-b border-slate-700">
                                          <span className="text-xs font-mono text-slate-500">Panel {panel.panel}</span>
                                          <span className="text-xl">{panel.emoji}</span>
                                        </div>
                                        <div className="p-3 flex flex-col gap-2 flex-1">
                                          {panel.caption && (
                                            <div className="bg-slate-700/60 rounded px-2 py-1">
                                              <p className="text-[10px] text-slate-400 italic text-center">{panel.caption}</p>
                                            </div>
                                          )}
                                          <div className="flex-1 flex flex-col justify-center py-2">
                                            <p className="text-xs text-violet-300 font-semibold text-center">{panel.characters}</p>
                                            <p className="text-xs text-slate-400 mt-1 text-center">{panel.action}</p>
                                            <p className="text-[10px] text-slate-600 mt-1 text-center">📍 {panel.setting}</p>
                                          </div>
                                          {panel.dialogue && (
                                            <div className="relative bg-white rounded-xl px-3 py-2">
                                              <div className="absolute -top-2 left-5 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[8px] border-l-transparent border-r-transparent border-b-white" />
                                              <p className="text-[11px] text-slate-800 font-medium text-center">{panel.dialogue}</p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 flex items-start gap-3">
                                  <span className="text-2xl">💡</span>
                                  <div>
                                    <p className="text-xs text-amber-400 uppercase tracking-wider font-semibold mb-1">Moral of the Story</p>
                                    <p className="text-slate-200 text-sm">{bundle.comicStrip.moralOfStory}</p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-300">
                                  <span>✨</span>
                                  <span><strong>FLUX + Canvas v3:</strong> 6 unique FLUX illustrations (one per panel scene) are generated in parallel, then composited into a 2×3 portrait grid by the Canvas API with title banner, captions, speech bubbles, and moral footer. Free, no API key needed.</span>
                                </div>
                              </>
                              );
                            })() : (
                              <div className="text-center py-12 text-slate-500">
                                <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                <p className="text-sm">No comic strip for this bundle.</p>
                                <p className="text-xs mt-1">Re-run the pipeline to generate one.</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ─ Newsletter Tab ─ */}
                        {activeTab === 'newsletter' && (
                          <div className="space-y-4">
                            {bundle.linkedInNewsletter ? (
                              <>
                                <div className="flex items-center justify-between">
                                  <h3 className="text-slate-100 font-semibold text-lg">{bundle.linkedInNewsletter.title}</h3>
                                  <button onClick={() => handleCopy([bundle.linkedInNewsletter!.title, bundle.linkedInNewsletter!.intro, bundle.linkedInNewsletter!.body, bundle.linkedInNewsletter!.cta].join('\n\n'))} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400 text-xs transition-colors">
                                    {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />} Copy
                                  </button>
                                </div>
                                <div className="bg-slate-800/60 rounded-xl p-4 space-y-3">
                                  <p className="text-slate-300 text-sm italic">{bundle.linkedInNewsletter.intro}</p>
                                  <div className="h-px bg-slate-700" />
                                  <div className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">{bundle.linkedInNewsletter.body}</div>
                                  <div className="h-px bg-slate-700" />
                                  <p className="text-violet-300 text-sm font-medium">📣 {bundle.linkedInNewsletter.cta}</p>
                                </div>
                              </>
                            ) : <div className="text-center py-10 text-slate-500 text-sm">Newsletter not generated — re-run the pipeline.</div>}
                          </div>
                        )}

                        {/* ─ Social Assets Tab ─ */}
                        {activeTab === 'social' && (
                          <div className="space-y-6">
                            {/* LinkedIn Carousel */}
                            {bundle.linkedInCarousel && (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-2"><Share2 className="w-4 h-4 text-blue-400" /> LinkedIn Carousel ({(bundle.linkedInCarousel?.slides ?? []).length} slides)</h4>
                                  <button onClick={() => handleCopy((bundle.linkedInCarousel?.slides ?? []).map((s,i) => `Slide ${i+1}: ${s.title}\n${s.body}`).join('\n\n'))} className="flex items-center gap-1 px-2.5 py-1 bg-slate-800 rounded-lg text-xs text-slate-400 hover:bg-slate-700 transition-colors">{copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />} Copy</button>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                  {(bundle.linkedInCarousel?.slides ?? []).map((slide, i) => (
                                    <div key={i} className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-3 space-y-1.5">
                                      <span className="text-[10px] text-violet-400 font-semibold">Slide {i+1}</span>
                                      <p className="text-xs text-slate-200 font-medium leading-tight">{slide.title}</p>
                                      <p className="text-[11px] text-slate-400 leading-snug">{slide.body}</p>
                                      {slide.visualNote && <p className="text-[10px] text-slate-600 italic">🎨 {slide.visualNote}</p>}
                                    </div>
                                  ))}
                                </div>
                                <div className="flex flex-wrap gap-1.5">{(bundle.linkedInCarousel?.hashtags ?? []).map(h => <span key={h} className="px-2 py-0.5 bg-blue-500/10 text-blue-300 text-[11px] rounded-full">{h}</span>)}</div>
                                <p className="text-xs text-slate-500">🗓 Banner concept: {bundle.linkedInCarousel.bannerConcept}</p>
                              </div>
                            )}
                            {/* X Thread */}
                            {bundle.xThread && (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-sky-400" /> X Thread ({(bundle.xThread?.tweets ?? []).length} tweets)</h4>
                                  <button onClick={() => handleCopy((bundle.xThread?.tweets ?? []).join('\n\n'))} className="flex items-center gap-1 px-2.5 py-1 bg-slate-800 rounded-lg text-xs text-slate-400 hover:bg-slate-700 transition-colors">{copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />} Copy</button>
                                </div>
                                <div className="space-y-2">{(bundle.xThread?.tweets ?? []).map((tw, i) => (
                                  <div key={i} className="bg-slate-800/60 border border-slate-700 rounded-xl px-4 py-3">
                                    <span className="text-[10px] text-sky-400 font-semibold">{i === 0 ? '📣 Hook' : `${i+1}/`}</span>
                                    <p className="text-sm text-slate-200 mt-1 leading-relaxed">{tw}</p>
                                    <p className="text-[10px] text-slate-600 mt-1 text-right">{tw.length} chars</p>
                                  </div>
                                ))}</div>
                                <div className="flex flex-wrap gap-1.5">{(bundle.xThread?.hashtags ?? []).map(h => <span key={h} className="px-2 py-0.5 bg-sky-500/10 text-sky-300 text-[11px] rounded-full">{h}</span>)}</div>
                              </div>
                            )}
                            {/* Meta / IG Story */}
                            {bundle.metaStory && (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-2"><Film className="w-4 h-4 text-fuchsia-400" /> Meta / IG Story &amp; Reel Caption</h4>
                                  <button onClick={() => handleCopy(bundle.metaStory!.reelCaption)} className="flex items-center gap-1 px-2.5 py-1 bg-slate-800 rounded-lg text-xs text-slate-400 hover:bg-slate-700 transition-colors">{copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />} Copy Caption</button>
                                </div>
                                <div className="bg-slate-800/60 rounded-xl p-4">
                                  <p className="text-xs text-fuchsia-400 font-semibold uppercase tracking-wider mb-2">📱 Story Slides</p>
                                  <div className="text-sm text-slate-200 whitespace-pre-wrap">{bundle.metaStory.storyContent}</div>
                                </div>
                                <div className="bg-slate-800/60 rounded-xl p-4">
                                  <p className="text-xs text-fuchsia-400 font-semibold uppercase tracking-wider mb-2">🎬 Reel Caption</p>
                                  <p className="text-sm text-slate-200">{bundle.metaStory.reelCaption}</p>
                                  <div className="flex flex-wrap gap-1.5 mt-2">{(bundle.metaStory?.hashtags ?? []).map(h => <span key={h} className="px-2 py-0.5 bg-fuchsia-500/10 text-fuchsia-300 text-[11px] rounded-full">{h}</span>)}</div>
                                </div>
                              </div>
                            )}
                            {!bundle.linkedInCarousel && !bundle.xThread && !bundle.metaStory && (
                              <div className="text-center py-10 text-slate-500 text-sm">Social assets not generated — re-run the pipeline.</div>
                            )}
                          </div>
                        )}

                        {/* ─ Visual Factory Tab ─ */}
                        {activeTab === 'visual' && (() => {
                          // Type-guard: visualPrompts must be a plain object with string fields
                          const vp = bundle.visualPrompts;
                          const isValidVP = vp && !Array.isArray(vp) && typeof (vp as Record<string,unknown>).square === 'string';
                          return (
                            <div className="space-y-4">
                              {isValidVP ? (
                                <>
                                  <p className="text-xs text-slate-500">Ready-to-use design prompts for Midjourney, Canva &amp; Ideogram. Copy each prompt to generate your cover image.</p>
                                  {([['Square (1080×1080)', vp!.square], ['Portrait (1080×1350)', vp!.portrait], ['Story (1080×1920)', vp!.story]] as [string, string][]).map(([label, prompt]) => (
                                    <div key={label} className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-2">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-semibold text-violet-400">{label}</span>
                                        <button onClick={() => handleCopy(prompt ?? '')} className="flex items-center gap-1 px-2.5 py-1 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs text-slate-400 transition-colors">{copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />} Copy Prompt</button>
                                      </div>
                                      <p className="text-sm text-slate-200 leading-relaxed">{prompt ?? '—'}</p>
                                    </div>
                                  ))}
                                  <div className="flex gap-2 flex-wrap">
                                    {['Midjourney', 'Ideogram', 'Canva'].map(tool => (
                                      <a key={tool} href={tool === 'Canva' ? 'https://www.canva.com/ai-image-generator/' : tool === 'Ideogram' ? 'https://ideogram.ai' : 'https://www.midjourney.com'} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-lg text-violet-300 text-xs hover:bg-violet-500/20 transition-colors">
                                        <ExternalLink className="w-3 h-3" /> Open {tool}
                                      </a>
                                    ))}
                                  </div>
                                </>
                              ) : <div className="text-center py-10 text-slate-500 text-sm">Visual prompts not generated — re-run the pipeline to generate them.</div>}
                            </div>
                          );
                        })()}

                        {/* ─ Video Workflow Tab ─ */}
                        {activeTab === 'video' && (
                          <div className="space-y-4">
                            {bundle.videoWorkflow ? (
                              <>
                                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4">
                                  <p className="text-xs text-cyan-400 font-semibold uppercase tracking-wider mb-1">🎣 Hook (first 5 seconds)</p>
                                  <p className="text-slate-200 text-sm font-medium">{bundle.videoWorkflow.hook}</p>
                                </div>
                                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Full Script</p>
                                    <button onClick={() => handleCopy(bundle.videoWorkflow!.script)} className="flex items-center gap-1 px-2.5 py-1 bg-slate-700 rounded-lg text-xs text-slate-400 hover:bg-slate-600 transition-colors">{copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />} Copy</button>
                                  </div>
                                  <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{bundle.videoWorkflow.script}</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div className="bg-slate-800/60 rounded-xl p-4">
                                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">🎥 Shot List</p>
                                    <div className="space-y-1.5">{(bundle.videoWorkflow?.shotList ?? []).map(shot => (
                                      <div key={shot.shot} className="flex items-start gap-2 text-xs">
                                        <span className="text-violet-400 font-mono w-8 flex-shrink-0">#{shot.shot}</span>
                                        <div><p className="text-slate-200">{shot.description}</p><p className="text-slate-500">{shot.duration}</p></div>
                                      </div>
                                    ))}</div>
                                  </div>
                                  <div className="bg-slate-800/60 rounded-xl p-4">
                                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">🎬 Scene Breakdown</p>
                                    <div className="space-y-1.5">{(bundle.videoWorkflow?.sceneBreakdown ?? []).map(scene => (
                                      <div key={scene.scene} className="text-xs border-l-2 border-violet-500/30 pl-2">
                                        <p className="text-slate-300 font-medium">{scene.setting}</p>
                                        <p className="text-slate-400">{scene.action}</p>
                                        {scene.text && <p className="text-slate-600 italic">“{scene.text}”</p>}
                                      </div>
                                    ))}</div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs text-slate-500">Optimised for:</span>
                                  {(bundle.videoWorkflow?.compatibility ?? ['VEED.io','Adobe Express','DaVinci Resolve']).map(c => {
                                    const label = c === 'CapCut' ? 'VEED.io' : c;
                                    return <span key={label} className="px-2.5 py-1 bg-slate-700 text-slate-300 text-xs rounded-lg">{label}</span>;
                                  })}
                                </div>
                              </>
                            ) : <div className="text-center py-10 text-slate-500 text-sm">Video workflow not generated — re-run the pipeline.</div>}
                          </div>
                        )}

                        {/* ─ Whitepaper Tab ─ */}
                        {activeTab === 'whitepaper' && (
                          <div className="space-y-4">
                            {bundle.whitepaperOutline ? (
                              <>
                                <div className="flex items-center justify-between">
                                  <h3 className="text-slate-100 font-semibold">{bundle.whitepaperOutline.title}</h3>
                                  <button onClick={() => handleCopy([bundle.whitepaperOutline!.title, bundle.whitepaperOutline!.executiveSummary, ...(bundle.whitepaperOutline?.sections ?? []).map(s => s.heading + '\n' + (s.keyPoints ?? []).join('\n')), bundle.whitepaperOutline!.conclusion].join('\n\n'))} className="flex items-center gap-1 px-2.5 py-1 bg-slate-800 rounded-lg text-xs text-slate-400 hover:bg-slate-700 transition-colors">{copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />} Copy</button>
                                </div>
                                <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4">
                                  <p className="text-xs text-violet-400 font-semibold uppercase tracking-wider mb-1">Executive Summary</p>
                                  <p className="text-slate-200 text-sm leading-relaxed">{bundle.whitepaperOutline.executiveSummary}</p>
                                </div>
                                <div className="space-y-2">{(bundle.whitepaperOutline?.sections ?? []).map((section, i) => (
                                  <div key={i} className="bg-slate-800/60 rounded-xl p-4">
                                    <p className="text-sm font-semibold text-slate-200 mb-2">{section.heading}</p>
                                    <ul className="space-y-1">{(section.keyPoints ?? []).map((pt, j) => <li key={j} className="flex items-start gap-2 text-sm text-slate-400"><span className="text-violet-400 mt-0.5">•</span>{pt}</li>)}</ul>
                                  </div>
                                ))}</div>
                                <div className="bg-slate-800/40 rounded-xl p-4">
                                  <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1">Conclusion</p>
                                  <p className="text-slate-200 text-sm">{bundle.whitepaperOutline.conclusion}</p>
                                </div>
                              </>
                            ) : <div className="text-center py-10 text-slate-500 text-sm">Whitepaper outline not generated — re-run the pipeline.</div>}
                          </div>
                        )}

                        {/* ─ Quality Score Badge ─ (shown on all tabs if available) */}
                        {bundle.qualityScores && (
                          <div className="mt-4 pt-4 border-t border-slate-800">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Humanization Score Matrix</p>
                              {bundle.qualityScores.repositoryUsed && (
                                <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] rounded-full">
                                  ✓ Knowledge Repo Used
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-7 gap-1.5">
                              {[
                                ['Brand Align', bundle.qualityScores.brandAlignment, 'text-violet-400'],
                                ['Humanization', bundle.qualityScores.humanization, 'text-cyan-400'],
                                ['Trust', bundle.qualityScores.trust, 'text-emerald-400'],
                                ['Conversion', bundle.qualityScores.conversion, 'text-amber-400'],
                                ['Repo Usage', bundle.qualityScores.repositoryUsed ? 100 : 0, 'text-rose-400'],
                                ['Anecdotal', bundle.qualityScores.anecdotalDensity, 'text-sky-400'],
                                ['Differentiation', bundle.qualityScores.differentiation, 'text-fuchsia-400'],
                              ].map(([label, score, color]) => (
                                <div key={String(label)} className="bg-slate-800/50 rounded-xl p-2 text-center">
                                  <p className={`text-base font-bold ${color}`}>{typeof score === 'boolean' ? (score ? '✓' : '✕') : score}</p>
                                  <p className="text-[9px] text-slate-500 leading-tight mt-0.5">{label}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {bundles.length === 0 && !isGenerating && (
          <div className="text-center py-16 bg-slate-900/50 rounded-xl border border-dashed border-slate-800">
            <Sparkles className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">No bundles generated yet</p>
            <p className="text-slate-600 text-sm mt-1">
              {hasApiKey ? 'Select a trend and run the pipeline above.' : 'Add your OpenAI key in Settings first.'}
            </p>
          </div>
        )}
      </div>

      {/* Lightbox overlay for full-size image */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxSrc(null)}
        >
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightboxSrc(null)}
              className="absolute -top-10 right-0 text-white/60 hover:text-white flex items-center gap-1.5 text-sm"
            >
              <X className="w-5 h-5" /> Close
            </button>
            <img
              src={lightboxSrc}
              alt="Generated infographic"
              className="w-full rounded-xl border border-violet-500/20 shadow-2xl"
            />
            <a
              href={lightboxSrc}
              download="infographic.png"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex items-center justify-center gap-2 w-full py-2.5 bg-violet-600 hover:bg-violet-500 rounded-lg text-white text-sm font-medium transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="w-4 h-4" /> Download PNG
            </a>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ContentPipeline;